import type { CSSProperties } from 'react';
import { ClipboardList, ExternalLink, MoreHorizontal } from 'lucide-react';
import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ContextMenu, MenuItem } from '@/shared/ui';
import css from './items-table.module.scss';

type DashboardRowMenuProps = {
  onOpenItinerary?: () => void;
  onOpenInOperations?: () => void;
};

const DROPDOWN_WIDTH = 200;

// Per-row "⋯" trigger → portalled ContextMenu (escapes the table's
// overflow:hidden). Position computed from the trigger rect on open.
export const DashboardRowMenu = ({
  onOpenItinerary,
  onOpenInOperations,
}: DashboardRowMenuProps) => {
  const [open, setOpen] = useState(false);
  const [offset, setOffset] = useState<CSSProperties | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const node = triggerRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    setOffset({ left: rect.right - DROPDOWN_WIDTH, top: rect.bottom + 4 });
  }, [open]);

  return (
    <>
      <button
        aria-label="Row actions"
        className={css.actionBtn}
        onClick={() => setOpen((prev) => !prev)}
        ref={triggerRef}
        type="button"
      >
        <MoreHorizontal size={16} />
      </button>
      {open &&
        offset &&
        typeof document !== 'undefined' &&
        createPortal(
          <>
            <button
              aria-label="Close menu"
              className={css.menuBackdrop}
              onClick={() => setOpen(false)}
              type="button"
            />
            <ContextMenu
              style={{
                ...offset,
                width: DROPDOWN_WIDTH,
                position: 'fixed',
                zIndex: 41,
              }}
            >
              {onOpenItinerary && (
                <MenuItem
                  icon={<ExternalLink size={14} />}
                  onClick={() => {
                    onOpenItinerary();
                    setOpen(false);
                  }}
                >
                  Open itinerary
                </MenuItem>
              )}
              {onOpenInOperations && (
                <MenuItem
                  icon={<ClipboardList size={14} />}
                  onClick={() => {
                    onOpenInOperations();
                    setOpen(false);
                  }}
                >
                  Open in Operations
                </MenuItem>
              )}
            </ContextMenu>
          </>,
          document.body
        )}
    </>
  );
};
